/**
 * Direction arrows on patches and reflights (MB's decision 1011, Q891 A, 3 Nov 2026; R192; docs/specs/map.md §5.1):
 * a patch or reflight still to fly carries arrows pointing the way it is to be flown, on Teslyn Field's map (the "Line
 * states" and "Lines remaining" layers), the phone's pre-flight brief map and Teslyn Cloud's staff project map. The
 * client portal's map has none.
 *
 * - The direction (decision 1013, Q892 B): the way the original line was actually flown, from its flown record (the
 *   logged run along the line, `start_along_m` to `end_along_m`, else the flown heading `direction_deg` against the
 *   line's bearing), so a tie flown west gets a patch pointing west even though its planned points run east. Only a
 *   line with no flown record yet takes its planned direction (its first point to its second, `rowBearing`). The same
 *   function gives the Refly list's "Direction" column (for example "270° (W)") and the arrow in its "Make a plan
 *   (.lns)" preview, so all three agree.
 * - The lines file keeps the planned point order: the nav system flies a line either way (F26048's planned traverses
 *   all run north in the block's `.lns` and were flown north and south in turn), so the direction is shown, not
 *   written into the file, and the files stay as MB's own Reflys files are laid out (decision 831).
 * - Where: one arrow in the middle of a line up to `ARROW_SPACING_M` long, so every patch has one; a longer line gets
 *   one per `ARROW_SPACING_M` (rounded down), evenly spaced and centred, so a 40 km reflight has eight, each about
 *   5 km apart. A drawing at a known scale (the brief's map) also keeps the arrows `ARROW_MIN_GAP_PX` apart, so a
 *   small map of a big block is not crowded.
 * - How: a filled arrowhead (`arrow` in `map-symbols.ts`) in the line's own colour (pink for a patch, yellow for a
 *   reflight) over the lines' thin dark edge (`map-line-casing`), so it reads over imagery and on the light maps in
 *   both themes. The line's points are given in the direction to fly, and the arrow turns with the line as drawn.
 * - Only lines whose feature names a direction (`MapFeature.direction_deg`) get arrows: never the original planned
 *   lines, the completed parts or the logged coverage.
 */
import { bearingWords, rowBearing } from './lns-files.js';
import type { LegendEntry, MapFeature } from './map-layers.js';
import type { LineStatus, PlannedLine } from './model.js';
import { ARROW_OUTLINE } from './map-symbols.js';
import type { LatLng } from './planning.js';

/** The parts of a line status that say how a line was flown. */
export type FlownRecord = Pick<
  LineStatus,
  | 'line_number'
  | 'planned_line_id'
  | 'direction_deg'
  | 'start_along_m'
  | 'end_along_m'
  | 'deleted_at'
  | 'created_at'
>;

type Ends = Pick<PlannedLine, 'x1' | 'y1' | 'x2' | 'y2'>;

/** The way a patch or reflight is to be flown. */
export interface PlannedDirection {
  /** Degrees from grid north, 0 to 359: the Refly list's "Direction". */
  readonly deg: number;
  /** From the original line's flown record, or (none yet) its planned point order. */
  readonly from: 'flown' | 'planned';
  /** To be flown from the original's second point to its first (as it was flown). */
  readonly againstPlan: boolean;
  /** The reflight's or patch's own points run against the original's. */
  readonly reversed: boolean;
}

/**
 * Whether a flown record went from the line's first point to its second (true), the other way (false), or cannot say
 * (null): the logged run along the line first, else the flown heading against the line's bearing.
 */
export function flownForward(
  status: Pick<FlownRecord, 'direction_deg' | 'start_along_m' | 'end_along_m'>,
  line: Ends,
): boolean | null {
  const { start_along_m: a, end_along_m: b } = status;
  if (a != null && b != null && a !== b) return b > a;
  if (status.direction_deg == null) return null;
  const gap = Math.abs(((status.direction_deg - rowBearing(line) + 540) % 360) - 180);
  return gap === 90 ? null : gap < 90;
}

/**
 * The direction to fly `original`'s reflight or patch `refly` (decision 1013): the way the original was flown, from the
 * newest of its live flown records in `statuses` that says (its own status rows: `planned_line_id` or the line's
 * number), else its planned direction. `reversed` says whether `refly`'s own points were saved the other way round.
 */
export function plannedDirection(
  original: Ends & Partial<Pick<PlannedLine, 'id' | 'number'>>,
  refly?: Ends | null,
  statuses: readonly FlownRecord[] = [],
): PlannedDirection {
  const dot = refly
    ? (refly.x2 - refly.x1) * (original.x2 - original.x1) +
      (refly.y2 - refly.y1) * (original.y2 - original.y1)
    : 0;
  const own = statuses
    .filter(
      (s) =>
        s.deleted_at === null &&
        ((original.id !== undefined && s.planned_line_id === original.id) ||
          (original.number !== undefined && s.line_number === original.number)),
    )
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
  let forward: boolean | null = null;
  for (const s of own) {
    forward = flownForward(s, original);
    if (forward !== null) break;
  }
  const planned = rowBearing(original);
  return {
    deg: forward === false ? (planned + 180) % 360 : planned,
    from: forward === null ? 'planned' : 'flown',
    againstPlan: forward === false,
    reversed: dot < 0,
  };
}

/** Two points in the original's order, turned to run the way the line is to be flown. */
export function alongDirection<T>(
  points: readonly [T, T] | null,
  d: Pick<PlannedDirection, 'againstPlan'>,
): [T, T] | null {
  if (!points) return null;
  return d.againstPlan ? [points[1], points[0]] : [points[0], points[1]];
}

/** Whether a lines file row's points run against `deg` (its preview arrow then sits at its first point). */
export function runsAgainst(row: Ends, deg: number): boolean {
  return Math.abs(((deg - rowBearing(row) + 540) % 360) - 180) > 90;
}

/** One arrow per this many metres of a line (rounded down, at least one). */
export const ARROW_SPACING_M = 5000;

/** On a drawing at a known scale (the brief's map), arrows at least this far apart, px. */
export const ARROW_MIN_GAP_PX = 80;

/** A line shorter than this (metres) has no direction worth drawing. */
const MIN_LENGTH_M = 1;

export interface ArrowSpacing {
  /** One arrow per this many metres (default `ARROW_SPACING_M`). */
  readonly spacingM?: number;
  /** Metres per pixel of a drawing at a known scale: arrows stay `ARROW_MIN_GAP_PX` apart on it. */
  readonly metresPerPx?: number;
}

/**
 * Where a line's arrows sit, as fractions 0 to 1 of its length from its first point: one in the middle of a line up
 * to the spacing long, else one per spacing, evenly spaced and centred. None on a line of no length.
 */
export function arrowStations(lengthM: number, opts: ArrowSpacing = {}): number[] {
  if (!(lengthM >= MIN_LENGTH_M)) return [];
  const spacing = Math.max(opts.spacingM ?? ARROW_SPACING_M, (opts.metresPerPx ?? 0) * ARROW_MIN_GAP_PX, 1);
  const n = Math.max(1, Math.floor(lengthM / spacing));
  return Array.from({ length: n }, (_, i) => (i + 0.5) / n);
}

/** A point on a plane, x to the right and y up. */
export type PlanePoint = readonly [number, number];

/** An arrow on a plane: where it sits and the way it points, degrees clockwise from up (north). */
export interface PlaneArrow {
  readonly x: number;
  readonly y: number;
  readonly rotation_deg: number;
}

/** Degrees clockwise from up of the way from `a` to `b` on a plane (y up), 0 to 360. */
export function planeBearing(a: PlanePoint, b: PlanePoint): number {
  const deg = (Math.atan2(b[0] - a[0], b[1] - a[1]) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/**
 * The arrows along a polyline on a plane (y up), at `stations` (fractions of its length from its first point), each
 * turned along the piece it sits on.
 */
export function planeArrows(points: readonly PlanePoint[], stations: readonly number[]): PlaneArrow[] {
  const pieces: { a: PlanePoint; b: PlanePoint; from: number; len: number }[] = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len === 0) continue;
    pieces.push({ a, b, from: total, len });
    total += len;
  }
  if (pieces.length === 0) return [];
  return stations.map((t) => {
    const d = Math.min(Math.max(t, 0), 1) * total;
    const p = pieces.find((q) => d <= q.from + q.len) ?? pieces[pieces.length - 1]!;
    const u = (d - p.from) / p.len;
    return {
      x: p.a[0] + (p.b[0] - p.a[0]) * u,
      y: p.a[1] + (p.b[1] - p.a[1]) * u,
      rotation_deg: planeBearing(p.a, p.b),
    };
  });
}

const RAD = Math.PI / 180;
/** Web Mercator, the maps' plane (Leaflet's): angles on it are angles on the screen. */
const merc = (p: LatLng): PlanePoint => [p.lng * RAD, Math.log(Math.tan(Math.PI / 4 + (p.lat * RAD) / 2))];
const unmerc = (x: number, y: number): LatLng => ({
  lat: (2 * Math.atan(Math.exp(y)) - Math.PI / 2) / RAD,
  lng: x / RAD,
});

/** Metres along a polyline of WGS 84 points (flat within each piece: survey lines are tens of kilometres). */
export function pathLengthM(points: readonly LatLng[]): number {
  let m = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const kx = 111_320 * Math.cos(((a.lat + b.lat) / 2) * RAD);
    m += Math.hypot((b.lng - a.lng) * kx, (b.lat - a.lat) * 110_574);
  }
  return m;
}

/** An arrow on a north-up map: where it sits and the way it points on screen, degrees clockwise from north. */
export interface MapArrow {
  readonly at: LatLng;
  readonly rotation_deg: number;
}

/** The arrows along a line drawn on a north-up map (Leaflet's Web Mercator), its points in the direction flown. */
export function mapArrows(points: readonly LatLng[], opts: ArrowSpacing = {}): MapArrow[] {
  const stations = arrowStations(pathLengthM(points), opts);
  return planeArrows(points.map(merc), stations).map((a) => ({
    at: unmerc(a.x, a.y),
    rotation_deg: Math.round(a.rotation_deg * 10) / 10,
  }));
}

/**
 * The arrows' legend row: the arrow in the patch's pink beside its words (a reflight's arrows are its yellow). Listed
 * on the maps that draw arrows, while a layer that has them is on.
 */
export const DIRECTION_ARROW_LEGEND: LegendEntry = {
  colour: 'map-line-to-fly',
  stroke: 'normal',
  dashed: false,
  word: 'Direction to fly (patches and reflights)',
  symbol: 'arrow',
};

/**
 * "Direction 270° (W), as the line was flown": the arrow's words, the Refly list's "Direction" with where it comes from
 * (a line with no flown record yet: "as planned, the line has not been flown").
 */
export const arrowWords = (deg: number, from: PlannedDirection['from'] = 'flown'): string =>
  `Direction ${bearingWords(deg)}, ${from === 'flown' ? 'as the line was flown' : 'as planned, the line has not been flown'}`;

/**
 * The direction arrows of the line features that name one (`direction_deg`: a patch or reflight still to fly): point
 * features in the line's layer and colour, drawn as the `arrow` symbol over the dark edge, turned along the line.
 * Pointing at one says the line's words and its direction; clicking it identifies the line, with its direction.
 */
export function directionArrowFeatures(
  features: readonly MapFeature[],
  opts: ArrowSpacing = {},
): MapFeature[] {
  const out: MapFeature[] = [];
  for (const f of features) {
    const deg = f.direction_deg;
    if (deg == null || f.geometry.type !== 'line') continue;
    const from = f.direction_from ?? 'flown';
    const words = arrowWords(deg, from);
    mapArrows(f.geometry.points, opts).forEach((a, i) => {
      out.push({
        id: `${f.id}:arrow:${i + 1}`,
        layer: f.layer,
        geometry: { type: 'point', at: a.at },
        style: {
          colour: f.style.colour,
          stroke: 'normal',
          dashed: false,
          fill: 0,
          symbol: 'arrow',
          rotation_deg: a.rotation_deg,
          casing: true,
          ...(f.style.faded ? { faded: true } : {}),
        },
        tooltip: `${f.tooltip} · ${words}`,
        identify: [
          ...f.identify,
          { label: 'Direction', value: bearingWords(deg) },
          {
            label: 'Direction from',
            value: from === 'flown' ? 'The way the line was flown' : 'The plan (not flown yet)',
          },
        ],
      });
    });
  }
  return out;
}

/** An arrow of a drawing in screen pixels (y down): a closed SVG path, `px` long, pointing `rotationDeg`. */
export function arrowPathAt(x: number, y: number, rotationDeg: number, px: number): string {
  // the `arrow` symbol's outline (map-symbols.ts) in its 24 by 24 box, centred on (12, 12), pointing up
  const k = px / 24;
  const c = Math.cos(rotationDeg * RAD);
  const s = Math.sin(rotationDeg * RAD);
  const pts = ARROW_OUTLINE.map(([ox, oy]) => {
    const dx = (ox - 12) * k;
    const dy = (oy - 12) * k;
    // clockwise on a screen whose y runs down
    return [x + dx * c - dy * s, y + dx * s + dy * c] as const;
  });
  return (
    pts.map(([px2, py2], i) => `${i === 0 ? 'M' : 'L'}${px2.toFixed(1)} ${py2.toFixed(1)}`).join(' ') + ' Z'
  );
}
